import React, { useRef, useState } from "react";
import { CSVLink } from "react-csv";
import RequestV2 from "../../../api/RequestV2";
import { extractExportRows } from "./exportUtils";
import ExportToolbarButton from "./ExportToolbarButton";

/**
 * CSV export — V2 styling, RequestV2 only. Props match legacy CsvDown (useV2 ignored).
 * When `exportRows` is set, the file is built from that array (e.g. current table page); no API call.
 * `exportRows` — if defined (including `[]`), CSV uses these rows only; `url` / `params` are ignored.
 * When `exportRows` is empty, the button is disabled (same as PdfDown with client export).
 */
export default function CsvDown({
  digit = 0,
  headers,
  url,
  params,
  type = "",
  title = "Download CSV",
  contentType,
  reportName = "Report.csv",
  method = "post",
  useV2: _useV2,
  exportRows,
  transformRows,
  disabled: disabledProp = false,
}) {
  const [data, setData] = useState([]);
  const [isDisabled, setIsDisabled] = useState(false);
  const csvLinkRef = useRef(null);

  const clientExport = exportRows !== undefined;
  const clientEmpty = clientExport && (!Array.isArray(exportRows) || exportRows.length === 0);
  const buttonDisabled = disabledProp || clientEmpty;

  const downloadReport = () => {
    if (isDisabled) return;
    if (clientExport && clientEmpty) return;

    if (clientExport) {
      setIsDisabled(true);
      const rows = Array.isArray(exportRows) ? exportRows : [];
      const finalData =
        digit === 1
          ? rows.map((v) => ({ ...v, transactionId: " txn_ " + (v.transactionId ?? "") }))
          : rows;
      setData(finalData);
      setTimeout(() => {
        csvLinkRef.current?.link?.click();
        setIsDisabled(false);
      }, 0);
      return;
    }

    setIsDisabled(true);

    const successHandler = (res) => {
      const rows = extractExportRows(res, { method, useV2: true });

      const transformedRows = transformRows
        ? transformRows(rows)
        : rows;

      if (transformedRows?.length) {
        const finalData =
          digit === 1
            ? transformedRows.map((v) => ({
              ...v,
              transactionId:
                " txn_ " + (v.transactionId ?? ""),
            }))
            : transformedRows;

        setData(finalData);

        setTimeout(
          () => csvLinkRef.current?.link?.click(),
          0
        );
      }

      setIsDisabled(false);
    };

    const errorHandler = () => {
      setIsDisabled(false);
    };

    const api = new RequestV2("", successHandler, errorHandler);
    if (method === "post") {
      api.post(url, params);
    } else {
      const query =
        params && Object.keys(params).length
          ? `?${new URLSearchParams(params).toString()}`
          : "";

      api.get(`${url}${query}`);
    }
  };

  const showButton = clientExport || url;

  /** Toolbar rows use gap on flex parents; Fragment made CSVLink + button two items and doubled spacing. Single wrapper fixes that. */
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 0 }}>
      <CSVLink data={data} headers={headers} filename={reportName} ref={csvLinkRef}>
        {""}
      </CSVLink>
      {showButton ? (
        <ExportToolbarButton loading={isDisabled} disabled={buttonDisabled} onClick={downloadReport}>
          {title}
        </ExportToolbarButton>
      ) : (
        <ExportToolbarButton disabled>
          {title}
        </ExportToolbarButton>
      )}
    </span>
  );
}
